import React from 'react';

export const CHUNK_RELOAD_SESSION_KEY = 'FIXOME_CHUNK_RELOAD_ONCE';

export function isChunkLoadError(error) {
  if (!error) {
    return false;
  }
  const name = String(error.name || '');
  const message = String(error.message || '');
  return (
    name === 'ChunkLoadError' ||
    /Loading chunk [\w-]+ failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message)
  );
}

export function clearChunkReloadGuard(storage = sessionStorage) {
  try {
    storage.removeItem(CHUNK_RELOAD_SESSION_KEY);
  } catch (err) {
    // Ignore storage failures (private mode).
  }
}

/**
 * Reload once after a stale-tab chunk failure. A sessionStorage flag blocks
 * a second automatic reload so a persistently missing chunk cannot loop.
 * Returns true when a reload was started.
 */
export function reloadOnceForChunkError(error, {
  storage = sessionStorage,
  reload = () => window.location.reload(),
} = {}) {
  if (!isChunkLoadError(error)) {
    return false;
  }

  let alreadyReloaded = false;
  try {
    alreadyReloaded = Boolean(storage.getItem(CHUNK_RELOAD_SESSION_KEY));
  } catch (err) {
    alreadyReloaded = false;
  }
  if (alreadyReloaded) {
    return false;
  }

  try {
    storage.setItem(CHUNK_RELOAD_SESSION_KEY, '1');
  } catch (err) {
    // Cannot persist the guard, so do not reload (that could loop).
    return false;
  }

  reload();
  return true;
}

export function loadWithChunkRetry(importer, options) {
  return Promise.resolve()
    .then(() => importer())
    .then((module) => {
      clearChunkReloadGuard(options && options.storage);
      return module;
    })
    .catch((error) => {
      if (reloadOnceForChunkError(error, options)) {
        return new Promise(() => {});
      }
      throw error;
    });
}

export function lazyWithRetry(importer) {
  return React.lazy(() => loadWithChunkRetry(importer));
}
